import type { ReactNode } from 'react';
import { Link } from 'react-router';
import type { ModerationRule } from '@spawnwire/shared';
import { RULE_LABEL } from '../../lib/moderation';
import { LEGAL } from './details';

// The three documents. Section ids are stable anchors (/privacy#cookies); the rule ids match MODERATION_RULES.

function Section({ id, title, children }: { id: string; title: string; children: ReactNode }) {
  return (
    <section>
      <h2 id={id}>{title}</h2>
      {children}
    </section>
  );
}

function Email() {
  return LEGAL.contactEmail.includes('@') ? <a href={`mailto:${LEGAL.contactEmail}`}>{LEGAL.contactEmail}</a> : <strong>{LEGAL.contactEmail}</strong>;
}

/** The email address, plus the in-app way to reach us. */
function Contact() {
  return (
    <>
      <Email /> or through <Link to="/support">Spawnwire Support</Link>
    </>
  );
}

function External({ href, children }: { href: string; children: ReactNode }) {
  return (
    <a href={href} target="_blank" rel="noopener noreferrer">
      {children}
    </a>
  );
}

function Rule({ id, children }: { id: ModerationRule; children: ReactNode }) {
  return (
    <li id={id}>
      <strong>{RULE_LABEL[id]}.</strong> {children}
    </li>
  );
}

export function CommunityRules() {
  return (
    <>
      <Section id="who" title="Who these rules are for">
        <p>
          Spawnwire is for hanging out with your friends and communities. These rules keep it that way. They apply to everyone, everywhere on Spawnwire: spaces, channels, forum posts,
          direct and group messages, profiles, names and server tags, voice and video calls, screen shares, and the Minecraft servers you run from Game Servers.
        </p>
        <p>
          The rules are part of the <Link to="/terms">Terms of Service</Link>, so breaking them breaks the Terms. Space owners can add their own rules for their space. Those can be
          stricter than these, never looser.
        </p>
        <p>
          Each rule below has the same name you'd see under <strong>Settings → Account standing</strong> if action was ever taken on your account.
        </p>
      </Section>

      <Section id="rules" title="The rules">
        <ol>
          <Rule id="spam">
            Don't flood channels, post the same thing over and over, mass-mention people, or send unwanted ads or invite links in DMs. Don't use scripts or bots to post from your
            account.
          </Rule>
          <Rule id="harassment">
            Don't insult, mock or target people, and don't keep contacting someone who wants to be left alone. Don't post someone's private information (real name, address, phone
            number, photos, IP address, school or workplace) without their permission, or threaten to. Don't get others to pile on someone.
          </Rule>
          <Rule id="hate">
            No attacks, slurs or dehumanising content about people because of race, ethnicity, nationality, religion, caste, sex, gender identity, sexual orientation, disability,
            age or serious illness. No hate symbols and no promoting hate groups.
          </Rule>
          <Rule id="violence">
            Don't threaten anyone, even as a joke. Don't encourage or celebrate violence, terrorism or violent extremism, and don't post gore.
          </Rule>
          <Rule id="self_harm">
            Don't encourage or promote suicide, self-harm or eating disorders. Talking about your own struggles is okay. If someone seems to be at risk, report it so staff can look,
            and if they're in immediate danger, contact local emergency services.
          </Rule>
          <Rule id="sexual">
            No pornography or sexually explicit content anywhere on Spawnwire, including profile pictures, names and screen shares. Don't send sexual messages to anyone who hasn't
            clearly asked for them. Anything sexual involving minors, including drawings and AI-made images, is removed straight away, the account is banned permanently, and we
            report it to the authorities.
          </Rule>
          <Rule id="scam">
            No scams, fake giveaways, trading scams, or attempts to get someone's password, login codes or payment details. Don't share malware or links to it.
          </Rule>
          <Rule id="cheating">
            Don't share, sell or advertise game cheats, hacks or stolen accounts. Don't exploit bugs in Spawnwire: report them to us at <Email /> instead.
          </Rule>
          <Rule id="impersonation">
            Don't pretend to be another person, a space's moderators, Spawnwire staff, or a brand or organisation. Parody and fan accounts are fine if they're clearly labelled.
          </Rule>
          <Rule id="server_abuse">
            Use your Minecraft server for playing Minecraft. No crypto mining, attacks on other computers (DDoS, port scanning), proxies or VPNs, file hosting, or other
            non-Minecraft workloads. Don't keep a server awake with fake players or bots so it never turns off when idle, and don't make extra accounts to get more servers.
          </Rule>
          <Rule id="other">
            Also not allowed: anything illegal where you live or where Spawnwire runs; sharing copyrighted material you don't have the right to share; selling drugs, weapons or
            other regulated goods; making a new account to get around a ban or restriction; and trying to break into accounts, Spawnwire or its servers, or overload them.
          </Rule>
        </ol>
      </Section>

      <Section id="spaces" title="Spaces and their moderators">
        <ul>
          <li>Every space has an owner, who can give roles and moderator powers to other members. Owners decide their space's own rules on top of these.</li>
          <li>
            Space moderators can delete messages, time members out (for up to 28 days), kick or ban people from the space, and turn on slow mode. Their actions are recorded in the
            space's audit log.
          </li>
          <li>
            Space moderators are members like you, not Spawnwire staff, and they can only act inside their own space. Warnings, restrictions and bans on your whole account come only
            from Spawnwire staff.
          </li>
          <li>
            Owners are responsible for keeping their space within these rules. Staff can remove a space that is built around breaking them, or whose moderators ignore serious
            problems.
          </li>
          <li>
            Communities that want to appear on <strong>Discover</strong> are reviewed by staff first, and can be turned down if the space or its description breaks these rules.
          </li>
          <li>
            Community spaces can set a <strong>server tag</strong> of up to 4 characters, which members can choose to wear next to their name. A tag follows the same rules as a
            name: no slurs, hate symbols or sexual references, and nothing that pretends to be Spawnwire staff or another community. Staff can act on a tag that breaks the rules
            the same way they act on a name.
          </li>
        </ul>
      </Section>

      <Section id="protect" title="Protecting yourself">
        <ul>
          <li>
            <strong>Block.</strong> A blocked person can't message you, call you or send you friend requests, and their mentions don't notify you. Their messages are hidden for
            you, with an option to show them. They aren't told you blocked them.
          </li>
          <li>
            <strong>Privacy settings.</strong> Choose who can send you direct messages and friend requests.
          </li>
          <li>
            <strong>Leave or mute.</strong> You can leave any space or group chat, and mute spaces and channels.
          </li>
          <li>
            <strong>Report.</strong> Report a message or a person from its menu, pick the closest reason and add details. Reports go to Spawnwire staff, and reports from inside a
            space also go to that space's moderators. The person you report isn't told who reported them.
          </li>
          <li>
            <strong>In an emergency,</strong> if someone is in immediate danger, contact local emergency services first, then report it.
          </li>
        </ul>
        <p>Please only report real problems. Making false reports on purpose, or spamming reports, is against the rules too.</p>
      </Section>

      <Section id="enforcement" title="What happens when rules are broken">
        <p>Space moderators can remove content and time out, kick or ban people from their space. Spawnwire staff can remove content anywhere and take action on your account:</p>
        <ul>
          <li>
            <strong>Warning.</strong> A note that you broke a rule. It counts toward your account standing for 90 days.
          </li>
          <li>
            <strong>Restriction.</strong> Blocks part of Spawnwire for a set time. A messaging restriction stops you sending or editing messages, reacting, uploading files, starting
            group chats and sending friend requests. A hosting restriction stops you creating, starting or changing game servers, and stops any that are running.
          </li>
          <li>
            <strong>Ban.</strong> You can still sign in, but you only see the reason, when the ban ends and a form to appeal. A ban also stops your game servers. Moderators can ban
            for up to 7 days; admins can ban for longer or permanently.
          </li>
          <li>
            <strong>Game servers and spaces</strong> that break the rules can be suspended, stopped or removed.
          </li>
        </ul>
        <p>
          We usually start small and step up if it keeps happening. Serious harm, such as sexual content involving minors, credible threats or attacks on Spawnwire, can lead to an
          immediate permanent ban and, where the law requires it, a report to the authorities.
        </p>
        <p>
          <strong>Account standing.</strong> Under <strong>Settings → Account standing</strong> you can see every action on your account, the rule it was under, the reason, and
          when it ends. Two active warnings, or a recent ban, puts your account "at risk".
        </p>
        <p>
          <strong>Appeals.</strong> You can appeal each action once, from Account standing or the ban screen. Staff look at it again and either lift the action or keep it.
        </p>
        <p>These rules may change as Spawnwire grows. We'll tell you in the app about important changes.</p>
      </Section>
    </>
  );
}

export function TermsOfService() {
  return (
    <>
      <Section id="agreement" title="1. Agreeing to these terms">
        <p>
          Spawnwire ("we", "us") is run from {LEGAL.country}. These Terms cover the Spawnwire website at chat.spawnwire.online, the Spawnwire apps for Windows and
          Android, and the Game Servers feature. By creating an account or using Spawnwire, you agree to these Terms, the <Link to="/rules">Community Rules</Link> and the{' '}
          <Link to="/privacy">Privacy Policy</Link>. If you don't agree, please don't use Spawnwire.
        </p>
        <p>
          <strong>Age.</strong> You must be at least {LEGAL.minimumAge} years old to use Spawnwire. If the law where you live sets a higher age for agreeing to terms like these
          yourself (for example 16 in some EU countries), a parent or guardian must agree for you until you reach it. If we learn that an account belongs to someone under{' '}
          {LEGAL.minimumAge}, we will close it.
        </p>
      </Section>

      <Section id="account" title="2. Your account">
        <ul>
          <li>
            Use an email address you control. Spawnwire doesn't verify email addresses or offer password reset by email yet, so keep your password safe: if you lose it, we may not
            be able to get your account back.
          </li>
          <li>An account is for one person. Don't share, sell or hand over your account. You're responsible for what happens on it.</li>
          <li>
            Pick a username and display name that don't break the Community Rules. We can change or take back a name that impersonates someone or is offensive. The usernames
            "spawnwire" and "spawnwire.system" are reserved.
          </li>
          <li>
            You can see where you're signed in and sign out other devices, and changing your password signs out everywhere else. Tell us at <Email /> if you think someone else got
            into your account.
          </li>
        </ul>
      </Section>

      <Section id="content" title="3. Your content">
        <ul>
          <li>What you post stays yours: messages, files, images, forum posts, your profile, spaces you set up, and files on your game server.</li>
          <li>
            So that Spawnwire can work, you give us a worldwide, non-exclusive, royalty-free licence to store, copy, process, transmit and display your content, only as needed to
            run Spawnwire. For example: showing your messages to people in the channel, making search and link previews work, and keeping backups. The licence ends when your
            content is deleted, except for copies kept in reports, moderation records and backups, and where the law requires us to keep it.
          </li>
          <li>You're responsible for what you post, and you must have the right to post it.</li>
          <li>
            We don't check content before it's posted, but we can remove content or limit access to it when it breaks these Terms, the Community Rules or the law.
          </li>
          <li>Messages and files are not end-to-end encrypted. Don't use Spawnwire for anything you need to keep secret.</li>
          <li>
            <strong>Copyright.</strong> If you believe something on Spawnwire infringes your copyright, email <Email /> with: what the work is, where it is on Spawnwire (a link or a
            description), your contact details, and a statement that you believe in good faith the use isn't authorised and that your notice is accurate. We will remove infringing
            material and close the accounts of repeat infringers.{LEGAL.copyrightAgent && <> {LEGAL.copyrightAgent}</>}
          </li>
          <li>If you send us ideas or feedback, we may use them without owing you anything.</li>
        </ul>
      </Section>

      <Section id="acceptable-use" title="4. Acceptable use">
        <p>
          Follow the <Link to="/rules">Community Rules</Link>. In addition, don't:
        </p>
        <ul>
          <li>try to get into accounts, data or parts of Spawnwire you aren't allowed to, or get around permissions, bans, restrictions or rate limits;</li>
          <li>run bots, scripts or scrapers through your account, or collect other people's information, without our written permission;</li>
          <li>overload or disrupt Spawnwire, its relay servers or game server hosting, or upload malware;</li>
          <li>use Spawnwire to break the law or to send bulk commercial messages;</li>
          <li>use Spawnwire while banned, or make new accounts to get around a ban or restriction.</li>
        </ul>
        <p>
          If you find a security problem, tell us privately at <Email /> and give us a reasonable time to fix it before telling anyone else. Don't access other people's data or
          disrupt the service while testing.
        </p>
      </Section>

      <Section id="spaces" title="5. Spaces and moderation">
        <ul>
          <li>
            When you create a space, you own it. You can set its rules, invite people, create roles and private channels, and moderate it. You're responsible for keeping your space
            within these Terms and the Community Rules.
          </li>
          <li>
            Anyone with an invite link can join the space it's for, so share links with care. You can limit how many times a link works and when it expires, and revoke it.
          </li>
          <li>
            Space moderators are members, not our staff. We don't review their decisions in their own space, but we can step in when a space breaks the Community Rules, including
            removing it.
          </li>
          <li>Being listed on Discover is optional and needs staff approval, which we can refuse.</li>
          <li>
            <strong>Server tags.</strong> Only community spaces can set a tag, and only people with permission to manage the space can change it. They're responsible for it
            following the <Link to="/rules">Community Rules</Link>. Wearing a tag is optional and you can take it off at any time. It also comes off by itself if you leave or are
            removed from the space, or the space removes its tag or stops being a community. Anyone who sees your name sees your tag and can open a card about the space, showing
            its name, icon, banner, member and online counts and when it was made.
          </li>
          <li>Before you can delete your account, you must hand over or delete the spaces you own.</li>
        </ul>
      </Section>

      <Section id="calls" title="6. Voice, video and screen sharing">
        <ul>
          <li>
            Calls always go through our relay server, so the other people in a call don't see your IP address. The <Link to="/privacy#calls">Privacy Policy</Link> explains how
            this works and what it doesn't hide.
          </li>
          <li>We don't record calls. Other people in a call could record it with other software. Don't record anyone without their permission where the law requires it.</li>
          <li>Screen sharing shows whatever screen or window you pick, including any notifications or private information on it. Check before you share.</li>
          <li>
            Call quality depends on your connection and ours, and calls can drop, for example when we restart the relay. We don't promise any level of call quality.
          </li>
        </ul>
      </Section>

      <Section id="files" title="7. Files, uploads and link previews">
        <ul>
          <li>Uploads have size and type limits, shown in the app. Some types, such as programs, scripts, HTML and SVG files, are refused.</li>
          <li>
            Files posted in a channel or conversation can only be opened by people who can see that channel or conversation. Profile pictures, banners and space icons are
            different: anyone who has their link can open them.
          </li>
          <li>
            Photos keep the information stored inside them, which can include where they were taken. Remove it before uploading if you don't want to share it. Profile pictures,
            banners and space icons you crop in Spawnwire are saved again, which removes that information; animated images are uploaded as they are.
          </li>
          <li>Files you upload but never send are deleted after 24 hours. Deleting a message deletes its files.</li>
          <li>When you post a link, our server may visit it to show a preview. The linked site sees our server, not you.</li>
          <li>Files and links from other people can be harmful. Open them at your own risk.</li>
        </ul>
      </Section>

      <Section id="game-servers" title="8. Game Servers (Minecraft hosting)">
        <ul>
          <li>Each account can run one free Minecraft Java server (Vanilla or Paper), with the memory and storage shown in the app. We can change these limits.</li>
          <li>
            To create a server you must accept the <External href="https://aka.ms/MinecraftEULA">Minecraft EULA</External>, and your server must follow it and Mojang's rules for
            servers.
          </li>
          <li>A server with nobody playing on it turns itself off after a few minutes. You can start it again at any time.</li>
          <li>
            Servers run on hardware from our hosting partner, {LEGAL.gameHost}. Your server's address is public, so anyone who knows it can try to join unless you use a whitelist.
          </li>
          <li>
            You're responsible for your server: who you let in, what happens on it, and the plugins and files you add. Only upload plugins and files you have the right to use.
          </li>
          <li>
            Players who join your server show up in its console and logs with their IP addresses. Keep that private. Sharing someone's IP address breaks the Community Rules.
          </li>
          <li>
            <strong>Keep your own backups.</strong> We don't promise to back up your server. Its world and files are deleted for good when you delete the server or your account, or
            if staff delete it for breaking the rules.
          </li>
          <li>
            Staff can look at your server's files, and stop, suspend or delete it, to enforce these Terms or protect Spawnwire and its hosting partner. A ban or hosting restriction
            stops your server.
          </li>
        </ul>
      </Section>

      <Section id="windows-app" title="9. The Windows and Android apps">
        <h3>Windows</h3>
        <ul>
          <li>
            You may install and use the Spawnwire app for Windows for free, to use Spawnwire. Download it only from{' '}
            <External href={`https://${LEGAL.website}`}>{LEGAL.website}</External>.
          </li>
          <li>
            The app updates itself. It checks for updates when it starts and every few hours, downloads them in the background and installs them when it restarts. Installing the
            app means you agree to these automatic updates.
          </li>
          <li>
            The app keeps you signed in by storing your session on your computer, encrypted by Windows. Anyone who can use your Windows account can open Spawnwire as you, so sign
            out on shared computers.
          </li>
          <li>The installer isn't code-signed yet, so Windows may show a warning the first time you run it.</li>
        </ul>
        <h3 id="android-app">Android</h3>
        <ul>
          <li>
            You may install and use the Spawnwire app for Android for free, to use Spawnwire. It isn't on Google Play: download it only from{' '}
            <External href={`https://${LEGAL.website}`}>{LEGAL.website}</External> or from inside the app. Android asks you to allow installing apps from that source.
          </li>
          <li>
            The app checks for new versions when you open it and tells you when one is out. You choose when to install it, and Android asks you to confirm. Older versions may miss
            features or stop working properly with Spawnwire, so keep it up to date.
          </li>
          <li>
            The app keeps you signed in by storing a sign-in token on your phone, protected by the Android Keystore. Anyone who can unlock your phone can open Spawnwire as you.
          </li>
          <li>
            Notifications are delivered through Google's Firebase Cloud Messaging and need Google Play services. We can't promise that every notification arrives, or arrives on
            time. The <Link to="/privacy#push">Privacy Policy</Link> explains what Google receives.
          </li>
        </ul>
      </Section>

      <Section id="availability" title="10. Availability and changes to Spawnwire">
        <ul>
          <li>Spawnwire is free. If we ever add paid features, they will come with their own terms and we won't charge you without your agreement.</li>
          <li>
            We work to keep Spawnwire running, but we don't promise it will always be available, fast or free of bugs. You can check its status at{' '}
            <External href={`https://${LEGAL.statusSite}`}>{LEGAL.statusSite}</External>.
          </li>
          <li>We can add, change or remove features and limits, such as upload sizes or game server resources.</li>
          <li>We can stop running Spawnwire. If we do, we'll give notice in the app {LEGAL.shutdownNotice} ahead where we reasonably can.</li>
        </ul>
      </Section>

      <Section id="closing" title="11. Suspension and closing your account">
        <ul>
          <li>
            You can delete your account at any time in Settings, using your password. You'll need to hand over or delete the spaces you own first. Deleting your account deletes
            your game server and can't be undone. Messages you sent stay where they were, shown as from "Deleted User". The <Link to="/privacy#deleting">Privacy Policy</Link>{' '}
            lists exactly what is removed.
          </li>
          <li>
            We can warn you, restrict or ban your account, remove your content, or stop, suspend or delete your game server, if you break these Terms, the Community Rules or the
            law, or to protect people or Spawnwire. You'll normally see the reason under Account standing and can appeal each action once. Where the harm is serious, or the law
            requires it, we may act without warning.
          </li>
          <li>If your account is closed, the parts of these Terms that by their nature should continue (such as sections 3, 12 and 13) still apply.</li>
        </ul>
      </Section>

      <Section id="liability" title="12. Disclaimers and limits of liability">
        <ul>
          <li>Spawnwire is provided "as is" and "as available", without warranties of any kind, to the extent the law allows.</li>
          <li>
            We aren't responsible for what other people post or do on Spawnwire, including in their spaces, calls and game servers, or for other websites, files and plugins they
            share.
          </li>
          <li>
            To the extent the law allows, we aren't liable for indirect, incidental, special, consequential or punitive damages, or for lost data, profits or goodwill. Our total
            liability for any claim about Spawnwire is limited to the greater of what you paid us in the 12 months before the claim, or {LEGAL.liabilityCap}.
          </li>
          <li>You agree to cover our reasonable costs from claims by others caused by your content or by your breaking these Terms.</li>
          <li>
            Some places don't allow some of these limits. Where that's so, they apply only as far as the law allows. Nothing in these Terms takes away rights you have as a consumer
            that can't be waived.
          </li>
        </ul>
      </Section>

      <Section id="changes" title="13. Changes to these Terms, law and contact">
        <ul>
          <li>
            We may update these Terms. We'll tell you in the app about important changes {LEGAL.changeNotice} before they take effect. If you keep using Spawnwire after that, you
            accept the new Terms. If you don't, you can delete your account.
          </li>
          <li>
            These Terms are governed by the laws of {LEGAL.governingLaw}, and disputes go to the courts of {LEGAL.courts}, unless the consumer law where you live gives you the
            right to use your local courts.
          </li>
          <li>If part of these Terms can't be enforced, the rest still applies. If we don't enforce something straight away, we can still enforce it later.</li>
          <li>These Terms, the Community Rules and the Privacy Policy are the whole agreement between you and us about Spawnwire.</li>
          <li>
            Questions: <Contact />.
          </li>
        </ul>
      </Section>
    </>
  );
}

export function PrivacyPolicy() {
  return (
    <>
      <Section id="who" title="Who we are and what this covers">
        <p>
          Spawnwire ("we", "us") is run from {LEGAL.country}. This policy explains what information we collect when you use the Spawnwire website
          at chat.spawnwire.online, the Spawnwire apps for Windows and Android, and Game Servers, why we collect it, who can see it, and the choices you have. You can reach us at <Contact />.
        </p>
        <p>
          <strong>The short version:</strong> we collect what a chat app needs to work. We share it only with the third-party providers that help us run Spawnwire, and when
          the law requires it. We don't sell your information, there are no ads or third-party trackers in Spawnwire, and the people you call can't see your IP address. Messages are not end-to-end encrypted, so don't use Spawnwire for secrets.
        </p>
      </Section>

      <Section id="collect" title="What we collect">
        <h3>What you give us</h3>
        <ul>
          <li>
            <strong>Account:</strong> email address, username, display name and password. We store only a salted hash of your password (argon2id), never the password itself.
          </li>
          <li>
            <strong>Profile (optional):</strong> profile picture, banner, bio, pronouns, custom status, name font, banner colour and the server tag you choose to wear.
          </li>
          <li>
            <strong>What you post:</strong> messages, replies, reactions, files, images, forum posts, pins and threads, and the spaces, channels, roles, invites and scheduled events you create,
            and the events you mark yourself interested in.
          </li>
          <li>
            <strong>Connections:</strong> friends, friend requests, people you block, and the spaces and conversations you're in.
          </li>
          <li>
            <strong>Settings:</strong> notification, privacy, appearance and audio settings, such as who can message you and your microphone preferences.
          </li>
          <li>
            <strong>Reports and appeals</strong> you send. A report stores a copy of the reported message's text.
          </li>
          <li>
            <strong>Support tickets:</strong> the email address, subject and messages you send to Spawnwire Support, and the game server it's about if you pick one. You can open
            a ticket without signing in.
          </li>
          <li>
            <strong>Game Servers:</strong> your server's name, software and version, the files you upload or edit, and the console commands you send.
          </li>
        </ul>
        <h3>What we collect automatically</h3>
        <ul>
          <li>
            <strong>Sign-ins:</strong> a session cookie, and for each signed-in device its browser or app description, when you signed in and when it was last used.
          </li>
          <li>
            <strong>IP address:</strong> used to keep the service safe (for example rate limits on sign-ins), recorded in our server logs, and seen by our call relay while you're
            in a call.
          </li>
          <li>
            <strong>Activity needed for features:</strong> whether you're online, idle or on Do Not Disturb, when you're typing, which call you're in and whether you're muted, and
            which messages you've read so unread markers work.
          </li>
          <li>
            <strong>Push notifications (Android app):</strong> if you allow notifications, a notification token for your phone from Google, the app's version, and whether you
            want message previews. See <a href="#push">Push notifications</a>.
          </li>
          <li>
            <strong>Logs:</strong> technical records of requests and errors. They record ids, not message content, passwords or cookies.
          </li>
        </ul>
        <p>
          <strong>What we don't collect:</strong> payment details (Spawnwire is free), your contacts, your precise location, or recordings of calls. Calls are not recorded.
        </p>
      </Section>

      <Section id="use" title="How we use it">
        <ul>
          <li>
            <strong>To run Spawnwire:</strong> deliver messages, show profiles, connect calls, host game servers and send notifications.
          </li>
          <li>
            <strong>To keep it safe:</strong> secure sign-ins, rate limits, fighting spam, handling reports and appeals, and enforcing the Terms and Community Rules.
          </li>
          <li>
            <strong>To fix and improve it:</strong> finding bugs and keeping the service running.
          </li>
          <li>
            <strong>To follow the law</strong> when we have to.
          </li>
        </ul>
        <p>
          We don't sell your personal information, share it with advertisers, or use it to build advertising profiles. Spawnwire has no ads and no third-party analytics or
          tracking. If that ever changes, we'll update this policy before it happens.
        </p>
        <p>
          <strong>For people in the EU and UK:</strong> we use your information to provide the service you signed up for (contract), to keep Spawnwire safe and working (our
          legitimate interests), and where the law requires it (legal obligation). Your browser asks for your consent before Spawnwire can use your microphone, camera or screen.
        </p>
      </Section>

      <Section id="who-sees" title="Who can see what">
        <ul>
          <li>
            <strong>People you share a space, conversation or friendship with</strong> can see your profile, display name and whether you're online. People with no connection to
            you can't open your profile.
          </li>
          <li>
            <strong>Anyone who sees your name</strong> also sees the server tag you wear, if any, and can open a card about that space: its name, icon, banner, tag, member and
            online counts and when it was made.
          </li>
          <li>
            <strong>People in a channel or conversation</strong> can see what you post there. Private channels are only visible to the roles and members the space chose.
          </li>
          <li>
            <strong>Space owners and moderators</strong> can see what their permissions allow in their space, its moderation audit log, and reports filed from inside it.
          </li>
          <li>
            <strong>Spawnwire staff</strong> (moderators and admins we appoint) use an admin panel that shows account details including email address, account standing, reports
            including the reported message, appeals, support tickets, game servers and their files, and spaces. It doesn't show private messages unless someone reports them. The actions staff take
            there are logged.
          </li>
          <li>
            <strong>Us, as the people who run the servers:</strong> messages and files are stored on our servers without end-to-end encryption, so they can technically be accessed.
            We only do that when we need to: to look into a report or abuse, to keep the service running, or because the law requires it.
          </li>
          <li>
            <strong>Third-party service providers.</strong> We use outside companies to run parts of Spawnwire, and your information may be shared with them so they can do that
            work for us. We share only what they need for it. They include {LEGAL.serverProvider}, which hosts our servers and database (and so stores everything above);{' '}
            {LEGAL.gameHost}, which runs the machines that host Game Servers and receives your server's name with your username, its files and its console; and{' '}
            {LEGAL.websiteHost}, which hosts the {LEGAL.website} website and the status page; and Google, which delivers notifications to the Android app (see{' '}
            <a href="#push">Push notifications</a>). We may add or change providers; ask us at <Email /> for the current list. When you
            post a link, our server may visit it to make a preview.
          </li>
          <li>
            <strong>Bots and webhooks a space adds.</strong> People who manage a space can add bots, which run on other people's servers and receive what they can see in that
            space, and webhooks, which can pass messages from channels everyone in the space can see to an outside address they choose. Those services are run by their owners, not
            by us, and have their own policies. Webhooks never pass on private channels or conversations.
          </li>
          <li>
            <strong>When the law requires it,</strong> or when it's needed to protect someone from serious harm, we may share information with authorities.
          </li>
          <li>
            <strong>If Spawnwire changes hands,</strong> your information moves with it and stays covered by this policy.
          </li>
        </ul>
      </Section>

      <Section id="calls" title="Calls and your IP address">
        <p>
          Every voice and video call and every screen share goes through our relay server, on the website and in the apps. The people in the call only see the relay's
          address, never yours: your app only offers the relay, and our server also strips any other address from what it passes between you. If the relay can't be reached, the
          call doesn't connect, rather than falling back to a direct connection.
        </p>
        <p>
          Audio and video are encrypted between the devices in the call, and the relay only forwards them. We don't record calls. The relay sign-in details your app gets are
          temporary and expire after about an hour.
        </p>
        <p>What this doesn't hide:</p>
        <ul>
          <li>
            <strong>From us:</strong> our relay and our servers see your IP address. They have to, in order to connect you.
          </li>
          <li>
            <strong>Outside calls:</strong> websites you open from links see your IP address as usual. Images in Spawnwire load only from Spawnwire itself, so reading messages
            doesn't contact anyone else's server.
          </li>
          <li>
            <strong>Minecraft:</strong> when you join someone's game server, its owner can see your IP address in the server's console and logs.
          </li>
          <li>
            <strong>Other software</strong> on your device, such as browser extensions, is outside our control.
          </li>
        </ul>
      </Section>

      <Section id="push" title="Push notifications">
        <p>
          The Android app can notify you when the app is closed. These notifications are delivered by Google's Firebase Cloud Messaging, so Google receives each one on its way
          to your phone. It's encrypted in transit, and Google's servers may be in the United States or other countries. The website and the Windows app don't use Google for
          notifications.
        </p>
        <ul>
          <li>
            <strong>Previews on</strong> (the default): a notification includes the sender's display name and a link to their profile picture, the name of the conversation or
            channel, and the start of the message.
          </li>
          <li>
            <strong>Previews off:</strong> it says only "New message", "New mention" or "New friend request". Google still gets the ids it needs to deliver it and to open the right
            conversation, but no names or message text. Change this for each phone under <strong>Settings → Notifications</strong>.
          </li>
          <li>
            We store your phone's notification token, the app version and the preview setting with that phone's sign-in. They're deleted when you sign out on that phone, when that
            sign-in expires, or when Google tells us the token no longer works. You can also turn notifications off in Android's settings.
          </li>
          <li>Spawnwire turns Firebase's analytics off, and we don't use Google for ads or tracking.</li>
        </ul>
      </Section>

      <Section id="cookies" title="Cookies and local storage">
        <ul>
          <li>
            Spawnwire uses one cookie, <code>spawnwire_sid</code>, which keeps you signed in. It's needed for the service to work, can't be read by scripts on the page, and ends
            when you sign out or after {LEGAL.sessionDays} days without use.
          </li>
          <li>
            Your browser also keeps some conveniences on your device: unsent drafts, which sections you've collapsed, recent emoji, the volume you set for each person in calls, and
            forum layout choices. These stay on your device and aren't sent to us. Clearing your browser's site data removes them.
          </li>
          <li>The Windows app keeps its sign-in and settings in its own folder on your computer, with the session cookie encrypted by Windows.</li>
          <li>
            The Android app keeps its sign-in token on your phone, protected by the Android Keystore, along with its settings. Signing out or uninstalling the app removes them.
          </li>
          <li>There are no advertising or analytics cookies.</li>
        </ul>
      </Section>

      <Section id="storage" title="Where your data is stored and how it's protected">
        <p>
          {LEGAL.serverCountry
            ? `Our servers are in ${LEGAL.serverCountry}, and some of our providers may be in other countries. If you use Spawnwire from somewhere else, including Australia, your information is sent to and stored there.`
            : 'Our servers, and those of our providers, may be outside Australia, so your information may be stored and handled overseas.'}
          {LEGAL.transferSafeguard && <> {LEGAL.transferSafeguard}</>}
        </p>
        <p>How we protect it:</p>
        <ul>
          <li>HTTPS for every connection.</li>
          <li>Passwords stored only as argon2id hashes, and sign-in tokens stored only as hashes.</li>
          <li>Every request checked on the server against what you're allowed to see; things you can't see look like they don't exist.</li>
          <li>Uploaded files checked by their actual contents and served in a way that stops them running code in your browser.</li>
          <li>Rate limits on sign-in, sign-up and sending, and a log of the actions staff take.</li>
        </ul>
        <p>
          What we don't have yet: two-factor sign-in, email verification, or end-to-end encryption of messages, and Spawnwire hasn't been independently audited. No service is
          perfectly secure. If a breach affects your personal information, we'll tell you and the authorities as the law requires.
        </p>
      </Section>

      <Section id="retention" title="How long we keep it">
        <table>
          <thead>
            <tr>
              <th>What</th>
              <th>How long</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Account and profile</td>
              <td>Until you delete your account</td>
            </tr>
            <tr>
              <td>Messages and files</td>
              <td>
                Until you, a moderator or the space owner deletes them. A deleted message and its files are removed from our database and storage (apart from backups).
              </td>
            </tr>
            <tr>
              <td>Files uploaded but never sent</td>
              <td>24 hours</td>
            </tr>
            <tr>
              <td>Sign-ins</td>
              <td>Until you sign out, or after {LEGAL.sessionDays} days without use</td>
            </tr>
            <tr>
              <td>Notification tokens (Android app)</td>
              <td>Until you sign out on that phone, that sign-in ends, or Google tells us the token no longer works</td>
            </tr>
            <tr>
              <td>Reports, moderation actions, appeals and the staff log</td>
              <td>{LEGAL.reportRetention}. A report keeps its copy of the message even if the original is deleted.</td>
            </tr>
            <tr>
              <td>Support tickets</td>
              <td>{LEGAL.supportRetention}</td>
            </tr>
            <tr>
              <td>Server logs</td>
              <td>{LEGAL.logRetention}</td>
            </tr>
            <tr>
              <td>Backups</td>
              <td>{LEGAL.backups}</td>
            </tr>
            <tr>
              <td>Game servers</td>
              <td>Until you or staff delete the server, or you delete your account</td>
            </tr>
          </tbody>
        </table>
        <p id="deleting">
          <strong>Deleting your account</strong> removes your email address, password, profile, picture and banner, settings, sign-ins, friends and blocks, space memberships,
          reactions, read markers, notification settings, unsent uploads and your game server. Your account then shows as "Deleted User". Messages you sent stay where they are,
          under "Deleted User", so conversations still make sense. Delete any messages you want gone before you delete your account. Reports and moderation records about your
          account may be kept as described above.
        </p>
      </Section>

      <Section id="rights" title="Your choices and rights">
        <p>In Spawnwire you can:</p>
        <ul>
          <li>edit your profile, and change your email, username and password;</li>
          <li>choose who can send you direct messages and friend requests, and block people;</li>
          <li>see where you're signed in and sign out other devices;</li>
          <li>control notifications, and delete your messages;</li>
          <li>delete your account.</li>
        </ul>
        <p>
          Depending on where you live (for example under Australia's Privacy Act, the GDPR in the EU and UK, or in California), you may also have the right to get a copy of
          your information, correct it, delete it, limit or object to how we use it, take it to another service, and complain to your privacy regulator (in Australia, the{' '}
          <External href="https://www.oaic.gov.au">Office of the Australian Information Commissioner</External>). Email <Email /> to use these rights. We'll reply
          within {LEGAL.replyTime}. There's no download-your-data button yet, so ask us and we'll send you a copy.
        </p>
        <p>
          Your browser, Windows or Android asks before Spawnwire can use your microphone, camera or screen, or (on Android) send you notifications, and you can take that permission back in their settings at any time.
        </p>
      </Section>

      <Section id="children" title="Children">
        <p>
          Spawnwire isn't for children under {LEGAL.minimumAge}. If we learn that we have information from a child under {LEGAL.minimumAge}, we'll delete the account. Parents or
          guardians who think their child has an account can contact <Email />.
        </p>
      </Section>

      <Section id="changes" title="Changes to this policy">
        <p>When we change this policy we'll update the date at the top, and for important changes we'll tell you in the app before they take effect.</p>
      </Section>

      <Section id="contact" title="Contact">
        <p>
          <strong>{LEGAL.operator}</strong>
          {LEGAL.postalAddress && (
            <>
              <br />
              {LEGAL.postalAddress}
            </>
          )}
          <br />
          <Email />
        </p>
        <p>
          You can also open a ticket with <Link to="/support">Spawnwire Support</Link>, signed in or not.
        </p>
      </Section>
    </>
  );
}
